import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@mui/material';
import { AddBoxIcon } from '@sharedCR/coreDesignSystem/icons';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import { entitySideDataTableStyles } from 'app/shared/componentsRestyled/common/form/shared/styles';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import useSort from 'hooks/useSort';
import { getConditionalObject } from 'utils/data';

import { PropertyAndLoanTestIds } from '../../../../../PropertyAndLoan.testIds';
import { tableWrapperStyles } from '../../../Content.styles';
import { getNotFoundTableData, prepareModifiedFields } from '../../../Content.utils';
import { pagesPropTypes } from '../../MainContent.constants';
import { otherExpensesModifiedFieldsToExclude } from '../PropertyExpenses.constants';
import { createNewTableData, getValuesToSave } from '../PropertyExpenses.utils';

import { getTableConfig } from './OtherExpenses.config';
import { defaultSortOrder, OtherExpensesInfo, TableColumnId } from './OtherExpenses.constants';
import OtherExpensesForm from './OtherExpensesForm';

const defaultObject = {};

const defaultPendingChanges = {};

const OtherExpenses = ({
  isLoading,
  isHidden = false,
  form = defaultObject,
  otherExpenses: otherExpensesTableData = null,
  setEntitySideData,
  setInitialEntitySideData,
  entitySideData,
  initialEntitySideData = defaultObject,
  setIsSavingAllowed,
  pendingChanges,
  setIsNestedEntityEditActive,
}) => {
  const [editableData, setEditableData] = useState(null);

  const { order, onRequestSort } = useSort(defaultSortOrder);

  const isFormDataEmpty = _.isEmpty(form);
  const entityData = isFormDataEmpty ? otherExpensesTableData : form;
  const isDataEmpty = _.isEmpty(entityData);
  const isDataLoading = isLoading && isDataEmpty;
  const { otherExpenses } = entitySideData;
  const isEditActive = !!editableData;
  const isOtherExpenseExist = !!otherExpenses?.length;
  const pendingChangesData = pendingChanges?.otherExpensesPendingChanges || defaultPendingChanges;

  useEffect(() => {
    setIsSavingAllowed(!isEditActive);
    setIsNestedEntityEditActive(isEditActive);

    return () => {
      setIsSavingAllowed(true);
    };
  }, [isEditActive, setIsSavingAllowed, setIsNestedEntityEditActive]);

  const otherExpensesInitialValuesMap = useMemo(() => {
    return initialEntitySideData.otherExpenses
      ? _.keyBy(initialEntitySideData.otherExpenses, 'id')
      : {};
  }, [initialEntitySideData]);

  const orderedOtherExpenseWithPendingChanges = useMemo(() => {
    const orderedOtherExpense = _.orderBy(otherExpenses, [order.id], [order.direction]);

    return prepareModifiedFields({
      currentData: orderedOtherExpense,
      pendingChangesData,
      initialValuesMap: otherExpensesInitialValuesMap,
      fieldsToExclude: otherExpensesModifiedFieldsToExclude,
    });
  }, [order.direction, order.id, pendingChangesData, otherExpenses, otherExpensesInitialValuesMap]);

  const correctedOtherExpenseTableData = isOtherExpenseExist
    ? orderedOtherExpenseWithPendingChanges
    : getNotFoundTableData(TableColumnId.NAME);

  useEffect(() => {
    if (!otherExpensesTableData) {
      return;
    }

    const isOtherExpenseExist = _.has(entitySideData, OtherExpensesInfo.KEY);

    if (isOtherExpenseExist) {
      return;
    }

    const dataToSet = {
      ...getConditionalObject(!isOtherExpenseExist, {
        otherExpenses: otherExpensesTableData,
      }),
    };

    setEntitySideData(prevState => ({
      ...prevState,
      ...dataToSet,
    }));
    setInitialEntitySideData(prevState => ({
      ...prevState,
      ...dataToSet,
    }));
  }, [entitySideData, otherExpensesTableData, setEntitySideData, setInitialEntitySideData]);

  const deleteTableData = useCallback(
    id => {
      setEntitySideData(prevState => ({
        ...prevState,
        otherExpenses: prevState?.otherExpenses.reduce((acc, entity) => {
          if (entity.id !== id) {
            acc.push(entity);

            return acc;
          }

          if (entity.isAdded) {
            return acc;
          }

          acc.push({ ...entity, isDeleted: true });

          return acc;
        }, []),
      }));

      setEditableData(null);
    },
    [setEntitySideData],
  );

  const tableConfig = getTableConfig({
    setEditableData,
    deleteTableData,
    editableRowId: editableData?.id,
    isActionDisabled: isLoading,
  });

  const addNewTableEntity = useCallback(() => {
    const entityData = createNewTableData();

    setEntitySideData(prevState => ({
      ...prevState,
      otherExpenses: [...(prevState?.otherExpenses ?? []), entityData],
    }));

    setEditableData(entityData);
  }, [setEntitySideData]);

  const cancelForm = useCallback(
    data => {
      if (data.isNewlyAdded) {
        setEntitySideData(prev => ({
          ...prev,
          otherExpenses: prev.otherExpenses.filter(({ id }) => id !== data.id),
        }));
      }

      setEditableData(null);
    },
    [setEntitySideData],
  );

  const saveForm = useCallback(
    ({ updatedData, initialData }) => {
      const dataToSave = getValuesToSave({ updatedData, initialData });

      setEntitySideData(prevState => ({
        ...prevState,
        otherExpenses: prevState?.otherExpenses?.map(otherExpense =>
          otherExpense.id === dataToSave.id ? dataToSave : otherExpense,
        ),
      }));

      setEditableData(null);
    },
    [setEntitySideData],
  );

  const additionalRowContent = editableData ? (
    <OtherExpensesForm
      cancelForm={cancelForm}
      data={editableData}
      deleteTableData={deleteTableData}
      isLoading={isLoading}
      pendingChanges={pendingChangesData[editableData.id]}
      saveForm={saveForm}
    />
  ) : null;

  // Hidden (chattel, LEN-156) still seeds entitySideData above, so the subject-property save sends the
  // existing rows back unchanged instead of an empty list.
  if (isHidden) {
    return null;
  }

  return (
    <>
      <TableCDS
        isHideTableWhenEmpty
        additionalRowContent={additionalRowContent}
        additionalRowContentRowId={editableData?.id}
        config={tableConfig}
        customStyles={entitySideDataTableStyles}
        data={correctedOtherExpenseTableData}
        isCustomDataDefinition={!isOtherExpenseExist}
        isLoading={isDataLoading}
        isSortDisabled={isEditActive}
        order={order}
        pendingChangesTextSelector="span"
        title={OtherExpensesInfo.TITLE}
        wrapperStyle={tableWrapperStyles}
        onRequestSort={onRequestSort}
      />
      {!isLoading && (
        <div className="mt-24 pb-24">
          <Button
            data-testid={PropertyAndLoanTestIds.OTHER_EXPENSES_ADD_BUTTON}
            disabled={isEditActive || isLoading}
            size="medium"
            startIcon={<AddBoxIcon size={IconSize[20]} />}
            variant="outlined"
            onClick={addNewTableEntity}
          >
            Add {OtherExpensesInfo.TITLE}
          </Button>
        </div>
      )}
    </>
  );
};

const usedProps = [
  'className',
  'entitySideData',
  'form',
  'initialEntitySideData',
  'isLoading',
  'pendingChanges',
  'setEntitySideData',
  'setInitialEntitySideData',
  'setIsSavingAllowed',
  'setIsNestedEntityEditActive',
];

OtherExpenses.propTypes = {
  ..._.pick(pagesPropTypes, usedProps),
  isHidden: PropTypes.bool,
  otherExpenses: PropTypes.array,
};

export default OtherExpenses;
